import React, { useEffect, useState, useRef } from 'react'
import Container from '../../Components/Container'
import toast, { Toaster } from 'react-hot-toast'
import {
    FaWarehouse,
    FaMapMarkerAlt,
    FaPhone,
    FaBoxes,
    FaEdit,
    FaTrash,
    FaPlus,
    FaSearch,
    FaTimes,
} from 'react-icons/fa'
import { motion, AnimatePresence } from 'framer-motion'

const readJson = (res) => {
    if (!res.ok) {
        throw new Error('Sunucu yanıt vermedi')
    }
    return res.json()
}

const isMutationError = (data) => {
    if (data == null || typeof data !== 'object') return false
    const st = data.status
    if (st === 400) return true
    if (typeof st === 'number' && st >= 400) return true
    return false
}

const Store = () => {
    const [open, setOpen] = useState(false)
    const [name, setName] = useState('')
    const [location, setLocation] = useState('')
    const [phone, setPhone] = useState('')
    const [capacity, setCapacity] = useState('')
    const [items, setItems] = useState([])
    const [id, setId] = useState(null)
    const [update, setUpdate] = useState(false)
    const [listLoading, setListLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const nameInputRef = useRef(null)

    const resetForm = () => {
        setName('')
        setLocation('')
        setPhone('')
        setCapacity('')
        setId(null)
        setUpdate(false)
    }

    const handleClose = () => {
        if (submitting) return
        setOpen(false)
        resetForm()
    }

    const openCreateModal = () => {
        resetForm()
        setOpen(true)
    }

    const openEditModal = (item) => {
        setUpdate(true)
        setId(item.id)
        setName(item.name ?? '')
        setLocation(item.location ?? '')
        setPhone(item.phone ?? '')
        setCapacity(item.capacity != null ? String(item.capacity) : '')
        setOpen(true)
    }

    const handleSubmit = () => {
        const n = name.trim()
        const loc = location.trim()
        const ph = phone.trim()
        const cap = capacity === '' ? '' : String(capacity).trim()

        if (!n || !loc || !ph || cap === '') {
            toast.error('Lütfen tüm alanları doldurun')
            return
        }

        setSubmitting(true)
        const formdata = new FormData()
        formdata.append('action', update ? 'update' : 'insert')
        formdata.append('name', n)
        formdata.append('location', loc)
        formdata.append('capacity', cap)
        formdata.append('phone', ph)
        if (update) formdata.append('id', id)

        toast.promise(
            fetch(process.env.REACT_APP_BASE_URL + 'store.php', {
                method: 'POST',
                body: formdata,
            })
                .then(readJson)
                .then((data) => {
                    if (isMutationError(data)) {
                        throw new Error(data.message || 'İşlem reddedildi')
                    }
                    fetchData()
                    handleClose()
                    return data
                }),
            {
                loading: update ? 'Güncelleniyor...' : 'Kaydediliyor...',
                success: update ? 'Depo güncellendi!' : 'Yeni depo eklendi!',
                error: (err) => err.message,
            }
        ).finally(() => setSubmitting(false))
    }

    const handleDelete = (item) => {
        toast(
            (t) => (
                <div className='flex flex-col gap-4'>
                    <p className='text-sm leading-relaxed text-slate-700'>
                        <span className='font-semibold text-slate-900'>
                            {item.name}
                        </span>{' '}
                        deposunu silmek istediğinize emin misiniz?
                    </p>
                    <div className='flex justify-end gap-2'>
                        <button
                            type='button'
                            className='rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50'
                            onClick={() => toast.dismiss(t.id)}
                        >
                            İptal
                        </button>
                        <button
                            type='button'
                            className='rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-700'
                            onClick={() => {
                                confirmDelete(item)
                                toast.dismiss(t.id)
                            }}
                        >
                            Sil
                        </button>
                    </div>
                </div>
            ),
            { duration: 8000 }
        )
    }

    const confirmDelete = (item) => {
        const formdata = new FormData()
        formdata.append('action', 'delete')
        formdata.append('id', item.id)
        formdata.append('name', item.name)

        toast.promise(
            fetch(process.env.REACT_APP_BASE_URL + 'store.php', {
                method: 'POST',
                body: formdata,
            })
                .then(readJson)
                .then((data) => {
                    if (isMutationError(data)) {
                        throw new Error(data.message || 'Silinemedi')
                    }
                    fetchData()
                    return data
                }),
            {
                loading: 'Siliniyor...',
                success: 'Depo başarıyla silindi!',
                error: (err) => err.message,
            }
        )
    }

    const fetchData = () => {
        setListLoading(true)
        const formdata = new FormData()
        formdata.append('action', 'select')

        fetch(process.env.REACT_APP_BASE_URL + 'store.php', {
            method: 'POST',
            body: formdata,
        })
            .then(readJson)
            .then((data) => {
                if (data != null && data.status === 400) {
                    toast.error(data.message || 'Liste alınamadı')
                    setItems([])
                    return
                }
                setItems(Array.isArray(data) ? data : [])
            })
            .catch(() => toast.error('Veriler yüklenirken bir hata oluştu'))
            .finally(() => setListLoading(false))
    }

    useEffect(() => {
        fetchData()
    }, [])

    useEffect(() => {
        if (!open) return
        const raf = requestAnimationFrame(() => nameInputRef.current?.focus())
        const onKey = (e) => {
            if (e.key === 'Escape' && !submitting) handleClose()
        }
        document.addEventListener('keydown', onKey)
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            cancelAnimationFrame(raf)
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = prevOverflow
        }
    }, [open, submitting])

    const q = searchQuery.toLowerCase().trim()
    const filteredItems = items.filter((item) => {
        const n = (item.name ?? '').toLowerCase()
        const loc = (item.location ?? '').toLowerCase()
        return !q || n.includes(q) || loc.includes(q)
    })

    const inputClass =
        'w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-60'

    return (
        <Container padding bg='#f8fafc'>
            <Toaster
                position='top-right'
                toastOptions={{
                    className: 'text-sm font-medium shadow-lg',
                }}
            />

            <AnimatePresence>
                {open && (
                    <motion.div
                        key='store-modal'
                        role='presentation'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className='fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 backdrop-blur-sm sm:items-center'
                        onClick={() => !submitting && handleClose()}
                    >
                        <motion.div
                            role='dialog'
                            aria-modal='true'
                            aria-labelledby='store-modal-title'
                            initial={{ scale: 0.96, opacity: 0, y: 12 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.96, opacity: 0, y: 12 }}
                            transition={{
                                type: 'spring',
                                damping: 26,
                                stiffness: 320,
                            }}
                            className='max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-2xl'
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className='border-b border-slate-100 px-6 py-5'>
                                <div className='flex items-start justify-between gap-3'>
                                    <div className='flex items-start gap-3'>
                                        <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600'>
                                            <FaWarehouse className='text-xl' />
                                        </div>
                                        <div>
                                            <h2
                                                id='store-modal-title'
                                                className='text-lg font-bold tracking-tight text-slate-900'
                                            >
                                                {update
                                                    ? 'Depo düzenle'
                                                    : 'Yeni depo'}
                                            </h2>
                                            <p className='mt-1 text-sm text-slate-600'>
                                                Konum, iletişim ve kapasite
                                                bilgilerini girin.
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type='button'
                                        onClick={() =>
                                            !submitting && handleClose()
                                        }
                                        disabled={submitting}
                                        className='rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50'
                                        aria-label='Kapat'
                                    >
                                        <FaTimes size={18} />
                                    </button>
                                </div>
                            </div>

                            <div className='space-y-4 px-6 py-6'>
                                <div>
                                    <label
                                        htmlFor='store-name'
                                        className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'
                                    >
                                        Depo adı
                                    </label>
                                    <input
                                        ref={nameInputRef}
                                        id='store-name'
                                        type='text'
                                        value={name}
                                        onChange={(e) =>
                                            setName(e.target.value)
                                        }
                                        className={inputClass}
                                        placeholder='Depo adı'
                                        disabled={submitting}
                                        autoComplete='off'
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor='store-location'
                                        className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'
                                    >
                                        Konum
                                    </label>
                                    <input
                                        id='store-location'
                                        type='text'
                                        value={location}
                                        onChange={(e) =>
                                            setLocation(e.target.value)
                                        }
                                        className={inputClass}
                                        placeholder='Adres veya bölge'
                                        disabled={submitting}
                                        autoComplete='off'
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor='store-capacity'
                                        className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'
                                    >
                                        Kapasite
                                    </label>
                                    <input
                                        id='store-capacity'
                                        type='number'
                                        min={0}
                                        value={capacity}
                                        onChange={(e) =>
                                            setCapacity(e.target.value)
                                        }
                                        className={inputClass}
                                        placeholder='Birim / palet vb.'
                                        disabled={submitting}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor='store-phone'
                                        className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'
                                    >
                                        Telefon
                                    </label>
                                    <input
                                        id='store-phone'
                                        type='tel'
                                        value={phone}
                                        onChange={(e) =>
                                            setPhone(e.target.value)
                                        }
                                        className={inputClass}
                                        placeholder='0xxx xxx xx xx'
                                        disabled={submitting}
                                        autoComplete='tel'
                                    />
                                </div>
                            </div>

                            <div className='flex justify-end gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4'>
                                <button
                                    type='button'
                                    onClick={handleClose}
                                    disabled={submitting}
                                    className='rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50'
                                >
                                    İptal
                                </button>
                                <button
                                    type='button'
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    className='inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:bg-indigo-700 disabled:pointer-events-none disabled:opacity-60'
                                >
                                    {submitting ? (
                                        <div className='h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent' />
                                    ) : (
                                        <>
                                            {update ? (
                                                <FaEdit size={16} />
                                            ) : (
                                                <FaPlus size={16} />
                                            )}
                                            {update ? 'Güncelle' : 'Ekle'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className='mx-auto w-full max-w-[1400px] space-y-6 px-4 pb-16 pt-2 sm:px-6 lg:px-8'>
                <div className='rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm shadow-slate-200/50 sm:p-8'>
                    <div className='flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between'>
                        <div className='flex items-start gap-4'>
                            <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-500/25'>
                                <FaWarehouse className='text-xl' />
                            </div>
                            <div>
                                <h1 className='text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl'>
                                    Depolar
                                </h1>
                                <p className='mt-1 max-w-xl text-sm leading-relaxed text-slate-600'>
                                    Tüm depolarınızı tek yerden yönetin; arama
                                    ile hızlıca bulun.
                                </p>
                                <p className='mt-3 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700'>
                                    {filteredItems.length} kayıt
                                    {q ? ' (filtrelendi)' : ''}
                                </p>
                            </div>
                        </div>
                        <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
                            <div className='relative min-w-[16rem]'>
                                <FaSearch className='pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
                                <input
                                    type='text'
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                    placeholder='Depo ara…'
                                    className={`${inputClass} pl-10`}
                                />
                            </div>
                            <button
                                type='button'
                                onClick={openCreateModal}
                                className='inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2'
                            >
                                <FaPlus size={16} />
                                Yeni depo
                            </button>
                        </div>
                    </div>
                </div>

                {listLoading && items.length === 0 ? (
                    <div className='flex flex-col items-center justify-center gap-3 rounded-2xl border border-slate-200/80 bg-white py-24 shadow-sm'>
                        <div className='h-10 w-10 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent' />
                        <p className='text-sm font-medium text-slate-600'>
                            Yükleniyor…
                        </p>
                    </div>
                ) : filteredItems.length > 0 ? (
                    <div className='grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
                        {filteredItems.map((item) => (
                            <motion.div
                                key={item.id}
                                layout
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className='group flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition hover:border-indigo-200/80 hover:shadow-md'
                            >
                                <div className='flex flex-1 flex-col p-6'>
                                    <h3 className='mb-4 text-lg font-bold tracking-tight text-slate-900'>
                                        {item.name}
                                    </h3>
                                    <div className='space-y-3 text-sm text-slate-600'>
                                        <div className='flex items-start gap-2.5'>
                                            <FaMapMarkerAlt className='mt-0.5 shrink-0 text-slate-400' />
                                            <span className='leading-snug'>
                                                {item.location}
                                            </span>
                                        </div>
                                        <div className='flex items-center gap-2.5'>
                                            <FaPhone className='shrink-0 text-slate-400' />
                                            <a
                                                href={`tel:${item.phone}`}
                                                className='font-medium text-indigo-600 hover:text-indigo-700'
                                            >
                                                {item.phone}
                                            </a>
                                        </div>
                                        <div className='flex items-center gap-2.5'>
                                            <FaBoxes className='shrink-0 text-slate-400' />
                                            <span>
                                                {item.capacity} birim kapasite
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className='mt-auto flex gap-2 border-t border-slate-100 bg-slate-50/80 p-4'>
                                    <button
                                        type='button'
                                        onClick={() => openEditModal(item)}
                                        className='flex flex-1 items-center justify-center gap-2 rounded-xl bg-indigo-50 px-3 py-2.5 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100'
                                    >
                                        <FaEdit size={15} />
                                        Düzenle
                                    </button>
                                    <button
                                        type='button'
                                        onClick={() => handleDelete(item)}
                                        className='flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-100'
                                    >
                                        <FaTrash size={15} />
                                        Sil
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                ) : (
                    <div className='flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-20 text-center shadow-sm'>
                        <div className='mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400'>
                            <FaWarehouse className='text-2xl' />
                        </div>
                        {searchQuery.trim() ? (
                            <>
                                <p className='text-base font-semibold text-slate-800'>
                                    Depo bulunamadı
                                </p>
                                <p className='mt-2 max-w-sm text-sm text-slate-600'>
                                    Farklı bir arama terimi deneyin.
                                </p>
                            </>
                        ) : (
                            <>
                                <p className='text-base font-semibold text-slate-800'>
                                    Henüz depo yok
                                </p>
                                <p className='mt-2 max-w-sm text-sm text-slate-600'>
                                    Yeni depo ekleyerek stok ve panel
                                    görünümlerinizi yapılandırın.
                                </p>
                                <button
                                    type='button'
                                    onClick={openCreateModal}
                                    className='mt-6 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-indigo-700'
                                >
                                    <FaPlus size={14} />
                                    Depo ekle
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </Container>
    )
}

export default Store
